import type { DeviceMapping } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { clearTruckDevice, getTruckDevice, setTruckDevice } from '../gps.api';
import { deviceChange, encodeDeviceChoice } from '../policies/device-choice.policy';

/** `taken`: someone put the device on another truck after the picker listed it. `failed`: anything else. */
export type TruckDeviceOutcome = 'unchanged' | 'set' | 'cleared' | 'taken' | 'failed';

/**
 * The truck form's device (gitaumoses4/chukua#422): the device on the truck now, and the call that moves it after the truck itself has saved —
 * fleet cannot make the call in one request, because tracking depends on fleet. A new truck has no `truckId` and starts with no device.
 */
export function useTruckDevice(truckId?: string) {
  const [current, setCurrent] = useState<DeviceMapping | null | undefined>(truckId ? undefined : null);

  useEffect(() => {
    if (!truckId) return;
    let cancelled = false;
    getTruckDevice(truckId)
      .catch(() => null)
      .then((device) => {
        if (!cancelled) setCurrent(device);
      });
    return () => {
      cancelled = true;
    };
  }, [truckId]);

  async function reload(): Promise<void> {
    if (truckId) setCurrent(await getTruckDevice(truckId).catch(() => null));
  }

  const choice = current ? encodeDeviceChoice(current) : undefined;

  /** `next` is the picker's value; `forTruckId` is the saved truck's id, which a new truck only has after it is created. */
  async function apply(forTruckId: string, next: string | undefined): Promise<TruckDeviceOutcome> {
    const change = deviceChange(choice, next);
    if (change.kind === 'none') return 'unchanged';
    try {
      if (change.kind === 'clear') await clearTruckDevice(forTruckId);
      else await setTruckDevice(forTruckId, change.choice);
      return change.kind === 'clear' ? 'cleared' : 'set';
    } catch (err) {
      return err instanceof ApiError && err.status === 409 ? 'taken' : 'failed';
    }
  }

  return { current, choice, reload, apply };
}
